import { SPOTLIGHT_TARGETS } from "../spotlightTargets";
import { SPOTLIGHT_EVENTS, SPOTLIGHT_SIGNAL_SCOPES } from "../spotlightEventNames";
import { emitSpotlightEvent } from "../spotlightBus";
import { REGISTRY_SETUP_STAGES } from "../../../constants/RegistrySetupStages";

const ID = {
	GRADFUND_SKIP: "gradfund-skip",
	BROWSE_CATALOGUE: "browse-catalogue",
	STARTER_CHOICES: "starter-choices",
	STARTER_ITEMS: "starter-items",
	CATALOGUE: "catalogue",
	ADD_SUCCESS: "add-success",
	REGISTRY_ITEMS: "registry-items",
	GOAL_TRACKER: "goal-tracker",
	GOAL_SET: "goal-set",
	GOAL_EDITOR: "goal-editor",
	GOAL_ACTIVATE: "goal-activate",
};

// The Create Your Registry flow publishes which view it is showing; closing it removes the signal.
const whenSetupStageIs = (stage, goto) => ({
	signal: { scope: SPOTLIGHT_SIGNAL_SCOPES.REGISTRY },
	when: (snapshot) => snapshot.setupStage === stage,
	goto,
});

const whenSetupClosed = (goto) => ({
	signal: { scope: SPOTLIGHT_SIGNAL_SCOPES.REGISTRY },
	when: (snapshot) => !snapshot.setupStage,
	goto,
});

const SKIP_SETUP_LABEL = "Skip for now";

// Skipping the starter portion jumps ahead, so the Create Your Registry modal has to be closed for the user.
const closeRegistrySetup = () => emitSpotlightEvent(SPOTLIGHT_EVENTS.REGISTRY_SETUP_CLOSE_REQUESTED);

const registryChapter = {
	id: "registry",
	title: "Registry",
	route: "Registry",
	routes: ["Registry", "VanityRegistry"],
	getRouteParams: ({ userData }) => ({ uid: userData?.uid }),
	// The Registry opens in a new tab from My Announcement, so the announcement tab stays where it is.
	followOnHandoff: false,
	isEligible: ({ userData }) => userData?.accountType === "Graduate",
	next: () => null,
	steps: [
		{
			id: "gradfund-setup",
			target: SPOTLIGHT_TARGETS.GRADFUND_SETUP_MODAL,
			skipIfTargetMissing: true,
			missingAfterMs: 4000,
			allowBack: false,
			title: "Graduation Fund Setup",
			body: "This is where you'll activate your Grad Fund, so friends and family can contribute toward your next chapter. You can activate it later, so there's no need to do it right now.",
			actionLabel: "Got it",
		},
		{
			id: ID.GRADFUND_SKIP,
			target: SPOTLIGHT_TARGETS.GRADFUND_SETUP_SKIP,
			interactive: true,
			skipIfTargetMissing: true,
			allowBack: false,
			title: "Skip for now",
			body: "For this tour, we'll skip the setup and come back to your Grad Fund later.",
			instruction: "Click Skip for Now to continue",
			waitFor: { event: SPOTLIGHT_EVENTS.GRADFUND_SETUP_SKIPPED },
		},
		{
			id: ID.BROWSE_CATALOGUE,
			target: SPOTLIGHT_TARGETS.REGISTRY_BROWSE_CATALOGUE,
			interactive: true,
			optional: true,
			skipLabel: SKIP_SETUP_LABEL,
			allowBack: false,
			title: "Browse Catalogue",
			body: "This is where you start building your Registry.",
			instruction: "Click Browse Catalogue to get started",
			skipTo: () => ID.GOAL_TRACKER,
			waitFor: { event: SPOTLIGHT_EVENTS.REGISTRY_SETUP_OPENED, goto: ID.STARTER_CHOICES },
		},
		{
			id: ID.STARTER_CHOICES,
			target: SPOTLIGHT_TARGETS.REGISTRY_STARTER_CHOICES,
			interactive: true,
			optional: true,
			skipLabel: SKIP_SETUP_LABEL,
			allowBack: false,
			title: "Start your Registry",
			body: "Begin with a ready-made starter list, or build your own from scratch. Pick whichever suits you.",
			instruction: "Choose an option to continue",
			skipTo: () => ID.GOAL_TRACKER,
			onAdvance: closeRegistrySetup,
			waitFor: [
				whenSetupStageIs(REGISTRY_SETUP_STAGES.STARTER, ID.STARTER_ITEMS),
				whenSetupStageIs(REGISTRY_SETUP_STAGES.CATALOGUE, ID.CATALOGUE),
				whenSetupClosed(ID.REGISTRY_ITEMS),
			],
		},
		{
			id: ID.STARTER_ITEMS,
			target: SPOTLIGHT_TARGETS.REGISTRY_STARTER_ITEMS,
			interactive: true,
			optional: true,
			skipLabel: SKIP_SETUP_LABEL,
			allowBack: false,
			title: "Your starter items",
			body: "We've preselected some useful starter items for you. Remove anything you don't want, then add the rest to your Registry.",
			instruction: "Add the selected items to continue",
			skipTo: () => ID.GOAL_TRACKER,
			onAdvance: closeRegistrySetup,
			waitFor: [
				whenSetupStageIs(REGISTRY_SETUP_STAGES.SUCCESS, ID.ADD_SUCCESS),
				whenSetupStageIs(REGISTRY_SETUP_STAGES.CHOOSE, ID.STARTER_CHOICES),
				whenSetupClosed(ID.REGISTRY_ITEMS),
			],
		},
		{
			id: ID.CATALOGUE,
			target: SPOTLIGHT_TARGETS.REGISTRY_CATALOGUE_MODAL,
			silent: true,
			interactive: true,
			optional: true,
			skipLabel: SKIP_SETUP_LABEL,
			allowBack: false,
			instruction: "Add items from the catalogue, or close it when you're done",
			skipTo: () => ID.GOAL_TRACKER,
			onAdvance: closeRegistrySetup,
			waitFor: [whenSetupStageIs(REGISTRY_SETUP_STAGES.SUCCESS, ID.ADD_SUCCESS), whenSetupClosed(ID.REGISTRY_ITEMS)],
		},
		{
			id: ID.ADD_SUCCESS,
			target: SPOTLIGHT_TARGETS.REGISTRY_ADD_SUCCESS,
			silent: true,
			interactive: true,
			allowBack: false,
			instruction: "Click Done to continue, or add more items first",
			waitFor: [whenSetupStageIs(REGISTRY_SETUP_STAGES.CATALOGUE, ID.CATALOGUE), whenSetupClosed(ID.REGISTRY_ITEMS)],
		},
		{
			id: ID.REGISTRY_ITEMS,
			target: SPOTLIGHT_TARGETS.REGISTRY_ITEMS_SECTION,
			skipIfTargetMissing: true,
			allowBack: false,
			title: "Your Registry items",
			body: "These are the items you've added to your Registry. You can add, remove, and manage your Registry items anytime.",
		},
		{
			id: ID.GOAL_TRACKER,
			target: SPOTLIGHT_TARGETS.REGISTRY_GOAL_TRACKER,
			allowBack: false,
			title: "My Goal Tracker",
			body: "This is where you set a Graduation Fund goal. Once you activate your Grad Fund, friends and family can send money toward it and you can receive it.",
		},
		{
			id: ID.GOAL_SET,
			target: SPOTLIGHT_TARGETS.REGISTRY_GOAL_SET,
			interactive: true,
			optional: true,
			skipLabel: SKIP_SETUP_LABEL,
			allowBack: false,
			title: "Set Your Goal",
			body: "Tell friends and family what you're working toward and how much you'd like to raise.",
			instruction: "Click Set Your Goal to continue",
			skipTo: () => ID.GOAL_ACTIVATE,
			waitFor: { event: SPOTLIGHT_EVENTS.REGISTRY_GOAL_EDITOR_OPENED, goto: ID.GOAL_EDITOR },
		},
		{
			id: ID.GOAL_EDITOR,
			target: SPOTLIGHT_TARGETS.GOAL_EDITOR,
			interactive: true,
			completeWhenTargetGone: ID.GOAL_ACTIVATE,
			allowBack: false,
			title: "Set up your goal",
			bullets: [
				{ label: "Target amount:", text: "how much you'd like to raise." },
				{ label: "Title:", text: "a name for your fund. This is optional." },
				{ label: "Description:", text: "what the money will go toward. This is optional." },
			],
			body: "Fill in whatever you'd like, then save.",
			instruction: "Save your goal, or cancel to skip this for now",
			waitFor: { event: SPOTLIGHT_EVENTS.REGISTRY_GOAL_SAVED, goto: ID.GOAL_ACTIVATE },
		},
		{
			id: ID.GOAL_ACTIVATE,
			target: SPOTLIGHT_TARGETS.REGISTRY_GOAL_ACTIVATE,
			skipIfTargetMissing: true,
			allowBack: false,
			title: "Activate Your Grad Fund",
			body: "You can prepare your goal now. Before visitors can send money, you'll need to activate your Grad Fund with Stripe. You can do that whenever you're ready.",
		},
		{
			id: "registry-yearbook",
			target: SPOTLIGHT_TARGETS.REGISTRY_YEARBOOK,
			skipIfTargetMissing: true,
			title: "Yearbook",
			body: "Just like on My Announcement, friends and family can leave you messages here.",
		},
		{
			id: "registry-share",
			target: SPOTLIGHT_TARGETS.REGISTRY_SHARE,
			skipIfTargetMissing: true,
			title: "Share your Registry",
			body: "You can share your MyTassel with friends and family anytime from here.",
		},
		{
			id: "tutorial-complete",
			completion: true,
			title: "Your MyTassel is ready to share! 🎓",
			body: "You've reached the end of setup. You can keep editing anything on your MyTassel at any time.",
		},
	],
};

export default registryChapter;
